// ─────────────────────────────────────────────────────────────────────────────
// EVERYTHING 420 — THE DESIGNER AGENT, the first mini-application.
//
// This is the hero of the Everything 420 demo. Eight screens, in order, of one designer
// turning a batch of supplier photos into publishable product-page image sets.
//
// WHY IT IS SHAPED LIKE THIS. Tomer told us plainly he is not worried about how
// we do it technically — he is worried whether it fits their people: "you do not
// use it if it does not suit human beings." Shuki: "our employees are not
// programmers." So every screen here answers that sentence, and where a choice
// was between more impressive and more obviously operable by a non-programmer,
// it went to operable.
//
// THE LADDER IS THE ARGUMENT. One image, wait. Second, wait. Third, wait. Only
// after three clean approvals does the agent ask to run the rest. Supervised,
// supervised, supervised, then trusted — which is Shuki's own sentence handed
// back to him working: "the first stage can be a bit more manual, the second
// stage is just approve and everything moves."
//
// THE THIRD PANEL IS THE PROOF. The design standard is the agent's own document,
// living in the AI data layer, not hardcoded in the app. When the designer says
// "more white space at the bottom", a line appears in that file, on screen,
// while you watch. Everybody claims their AI learns from feedback; almost nobody
// shows the file changing. It is also the honest answer to the adoption fear:
// a designer can READ what the agent believes their taste is, and edit it.
//
// ⚠️ EVERYTHING HERE IS ILLUSTRATIVE. We have no product photography from them
// and no design standard from them — the products are deliberately generic
// drawn shapes, not their merchandise, and the standard is a plausible example
// of the artifact, not a claim about their taste. Every screen carries the
// PROTOTYPE chip. With a CTO on the other side, an honest mock beats a polished
// one he later sees through.
//
// ONE component renders every screen: <DesignerScreen step={n} />. The stepper
// and the filmstrip on the board both use it. The FILM has its own copy at
// everything-420-video/src/app.tsx, rebuilt for motion — when the styling
// changes here it must change there too, or the board and the film disagree.
//
// Owner agent: Ruby.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import type React from "react";

// ─── the Notion palette (Erez, 2026-07-28) ───────────────────────────────────
// Restyled from a navy-rail SaaS look to Notion's language. The change is not
// only colour, and copying the hexes without the rest gets you a recolour that
// still does not read as Notion. What actually makes it:
//
//   · WARM greys, not blue-greys. The ink is #37352F — a warm near-black — and
//     every neutral is derived from it at low alpha, so the whole surface has
//     one temperature.
//   · NOTHING IS DARK. The rail and the chat were navy panels; Notion has no
//     dark panels. Separation comes from a half-tone background and a hairline,
//     never from inverting a block.
//   · HAIRLINES, NOT SHADOWS. One 1px rgba border. Shadow is reserved for
//     things that genuinely float (a popover), and the app has none.
//   · SMALL RADII. 3px on a tag, 4px on a button, 6px on a card. The old 12-14px
//     is what makes an interface look like a mobile app rather than a document.
//   · TYPE CARRIES HIERARCHY, not weight-800 and letterspacing. 600 is the
//     heaviest thing on the screen.
const A = {
  appBg: "#FFFFFF",            // the page itself is white; the RAIL is the tinted one
  panel: "#FFFFFF",
  soft: "#F7F7F5",             // Notion's sidebar / code / hover tint
  softer: "#F1F1EF",           // the grey block background
  border: "#E9E9E7",           // the hairline
  ink: "#37352F",              // primary text — warm, not blue
  ink2: "rgba(55,53,47,0.65)", // secondary
  ink3: "rgba(55,53,47,0.45)", // tertiary / placeholder
  hover: "rgba(55,53,47,0.06)",
  selected: "rgba(55,53,47,0.08)",
  // NO BLUE (Erez, 2026-07-28: "it's still blue"). Notion does use blue, but on
  // a screen this neutral it was the one saturated thing and the eye read the
  // whole app as blue — the operator's chat bubble especially, which looked
  // like a messaging app. Colour now means something: green = accepted or
  // written, amber = waiting on a person, red = exception. Everything else is
  // grey. Do not reintroduce an accent without a meaning attached to it.
  green: "#448361", greenBg: "#EDF3EC",
  amber: "#CB912F", amberBg: "#FAEBDD",
  red: "#D44C47", redBg: "#FBE4E4",
  violet: "#9065B0", violetBg: "#F4F0F7",
};

export const SCREEN_W = 1760;
// 980 left the later screens visibly bottom-empty — a third of the body was
// blank once the gallery had finished. 840 is sized to the fullest screen (8)
// and still gives the empty state on screen 1 room to breathe.
export const SCREEN_H = 840;

// ─── the column budget ───────────────────────────────────────────────────────
// The gallery is what is LEFT after the rail, the chat and the standard panel,
// so card sizes are DERIVED from it rather than eyeballed. Eyeballing them is
// what broke screen 7: three pair-cards came to 1069px in an 806px column and
// painted straight over the chat panel. These four numbers are the single source
// for the panel widths below — change one here, everything re-fits.
const RAIL_W = 186, CHAT_W = 348, STD_W = 384, BODY_PAD = 18;
const GALLERY_W = SCREEN_W - RAIL_W - CHAT_W - STD_W - BODY_PAD * 2;   // 806
const PAIR_CHROME = 12 * 2 + 9;                                        // card padding + inner gap

/** Largest square image that fits `n` pair-cards across the gallery. */
const pairImage = (n: number, gap: number) =>
  Math.floor((GALLERY_W - gap * (n - 1) - PAIR_CHROME * n) / (2 * n));

const PAIR_SMALL = pairImage(3, 11);   // three across — the step-7 row
const PAIR_BIG = 250;                  // one pair beside its explanation panel

// Notion's own stack. It is a UI font, not a brand font — which is the point.
const FONT = "ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// ─── the OS rail ─────────────────────────────────────────────────────────────
// The mini-app is INSIDE the operating system, and the rail is what says so:
// connectors, the data layer, agents and runs are all one click away. That is
// the whole product argument in a piece of navigation.
type NavRow = { k?: string; label?: string; icon?: string; group?: string; sub?: boolean };
const NAV: NavRow[] = [
  { group: "SEE" },
  { k: "home", icon: "🏠", label: "Home" },
  { k: "ask", icon: "✦", label: "Ask" },
  { group: "MINI-APPS" },
  { k: "designer", icon: "🖼", label: "Designer" },
  { k: "listings", icon: "🏷", label: "Listings", sub: true },
  { k: "restock", icon: "📦", label: "Restock", sub: true },
  { group: "RUN" },
  { k: "runs", icon: "▶", label: "Runs" },
  { k: "workflows", icon: "⚙", label: "Workflows" },
  { k: "agents", icon: "◇", label: "Agents" },
  { group: "DATA" },
  { k: "brain", icon: "🧠", label: "Data layer" },
  { k: "connectors", icon: "🔌", label: "Connectors" },
];

function Rail() {
  return (
    // Notion's sidebar: the ONE tinted surface, separated by a hairline rather
    // than by being dark. Selected = a low-alpha wash of the ink colour, with no
    // accent bar — Notion never marks the current page with a coloured edge.
    <div style={{ width: RAIL_W, background: A.soft, flexShrink: 0, display: "flex", flexDirection: "column",
      padding: "10px 0", borderRight: `1px solid ${A.border}` }}>
      <div style={{ padding: "4px 10px 10px", margin: "0 4px", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 20, height: 20, borderRadius: 3, background: A.ink, display: "inline-flex",
          alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#FFF" }}>E</span>
        <div style={{ fontSize: 14, fontWeight: 600, color: A.ink }}>Everything 420</div>
        <span style={{ marginLeft: "auto", fontSize: 11, color: A.ink3 }}>⌄</span>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {NAV.map((r, i) =>
          r.group ? (
            <div key={i} style={{ padding: "14px 14px 4px", fontSize: 11, fontWeight: 500, color: A.ink3 }}>
              {r.group?.[0] + r.group!.slice(1).toLowerCase()}
            </div>
          ) : (
            <div key={i} style={{
              margin: "0 4px", padding: r.sub ? "5px 8px 5px 26px" : "5px 8px", borderRadius: 4,
              display: "flex", alignItems: "center", gap: 8,
              background: r.k === "designer" ? A.selected : "transparent",
            }}>
              <span style={{ fontSize: 13, width: 16, textAlign: "center" }}>{r.icon}</span>
              <span style={{ fontSize: 14, fontWeight: r.k === "designer" ? 500 : 400,
                color: r.k === "designer" ? A.ink : A.ink2 }}>{r.label}</span>
            </div>
          ),
        )}
      </div>
      <div style={{ padding: "8px 14px 0", marginTop: 8 }}>
        <div style={{ fontSize: 12, color: A.ink3, lineHeight: 1.5 }}>7 connectors live<br />brain updated 4 min ago</div>
      </div>
    </div>
  );
}

// ─── the generic products ────────────────────────────────────────────────────
// Deliberately DRAWN, not photographed, and deliberately not their merchandise.
// We have no supplier photos from them; inventing product imagery and presenting
// it as theirs is the fastest way to lose a CTO's trust. Shapes carry the point
// — bad frame versus good frame — without pretending to be a real catalogue.
type Kind = "jar" | "pouch" | "box" | "bottle";

function Product({ kind, scale = 1 }: { kind: Kind; scale?: number }) {
  const s = (n: number) => n * scale;
  const label = <rect x={s(10)} y={s(38)} width={s(44)} height={s(30)} rx={s(3)} fill="#FFFFFF" opacity={.9} />;
  const common = { stroke: "#00000018", strokeWidth: 1 };
  if (kind === "jar")
    return (
      <svg width={s(64)} height={s(96)} viewBox={`0 0 ${s(64)} ${s(96)}`} style={{ display: "block" }}>
        <rect x={s(16)} y={s(4)} width={s(32)} height={s(12)} rx={s(3)} fill="#3F4A55" />
        <rect x={s(8)} y={s(14)} width={s(48)} height={s(76)} rx={s(9)} fill="#6E8C6A" {...common} />
        {label}
      </svg>
    );
  if (kind === "pouch")
    return (
      <svg width={s(64)} height={s(96)} viewBox={`0 0 ${s(64)} ${s(96)}`} style={{ display: "block" }}>
        <path d={`M ${s(12)} ${s(10)} L ${s(52)} ${s(10)} L ${s(56)} ${s(90)} L ${s(8)} ${s(90)} Z`} fill="#8A6E9C" {...common} />
        <rect x={s(14)} y={s(4)} width={s(36)} height={s(8)} rx={s(2)} fill="#6B5480" />
        {label}
      </svg>
    );
  if (kind === "box")
    return (
      <svg width={s(64)} height={s(96)} viewBox={`0 0 ${s(64)} ${s(96)}`} style={{ display: "block" }}>
        <rect x={s(10)} y={s(18)} width={s(44)} height={s(70)} rx={s(4)} fill="#B08A5A" {...common} />
        <path d={`M ${s(10)} ${s(18)} L ${s(20)} ${s(8)} L ${s(64)} ${s(8)} L ${s(54)} ${s(18)} Z`} fill="#C79E6A" />
        {label}
      </svg>
    );
  return (
    <svg width={s(64)} height={s(96)} viewBox={`0 0 ${s(64)} ${s(96)}`} style={{ display: "block" }}>
      <rect x={s(26)} y={s(4)} width={s(12)} height={s(16)} rx={s(2)} fill="#3F4A55" />
      <path d={`M ${s(22)} ${s(20)} L ${s(42)} ${s(20)} L ${s(50)} ${s(40)} L ${s(50)} ${s(90)} L ${s(14)} ${s(90)} L ${s(14)} ${s(40)} Z`} fill="#5A7E96" {...common} />
      {label}
    </svg>
  );
}

/** The supplier's photo: whatever the factory sent. Off-centre, a coloured
 *  surface, another product half in frame, a hard shadow. This is the input. */
function RawShot({ kind, w, h }: { kind: Kind; w: number; h: number }) {
  return (
    <div style={{ width: w, height: h, borderRadius: 4, overflow: "hidden", position: "relative",
      background: "linear-gradient(155deg,#9C907F 0%,#7E7466 55%,#6B6357 100%)" }}>
      {/* a neighbour, half out of frame — the clutter that has to go */}
      <div style={{ position: "absolute", left: -w * .12, bottom: h * .06, opacity: .85, transform: "rotate(-7deg)" }}>
        <Product kind="box" scale={h / 150} />
      </div>
      <div style={{ position: "absolute", left: "56%", top: "52%", transform: "translate(-50%,-50%) rotate(5deg)",
        filter: "drop-shadow(6px 10px 6px rgba(0,0,0,.42))" }}>
        <Product kind={kind} scale={h / 132} />
      </div>
      <div style={{ position: "absolute", left: 7, top: 7, fontFamily: MONO, fontSize: 9, fontWeight: 700,
        color: "#FFFFFFCC", background: "#00000055", padding: "2px 6px", borderRadius: 3 }}>Supplier</div>
    </div>
  );
}

/** What the standard asks for: white, centred, even margins, soft contact
 *  shadow, packaging never cropped. This is the output. */
function CleanShot({ kind, w, h, pad, guides }: { kind: Kind; w: number; h: number; pad?: number; guides?: boolean }) {
  const bottom = pad ?? h * .1;
  return (
    <div style={{ width: w, height: h, borderRadius: 4, overflow: "hidden", position: "relative",
      background: "#FFFFFF", border: `1px solid ${A.border}` }}>
      <div style={{ position: "absolute", left: "50%", bottom, transform: "translateX(-50%)",
        filter: "drop-shadow(0 5px 7px rgba(0,0,0,.13))" }}>
        <Product kind={kind} scale={h / 152} />
      </div>
      {guides && (
        <>
          <div style={{ position: "absolute", left: 0, right: 0, bottom, height: 1, background: "rgba(68,131,97,.4)" }} />
          <div style={{ position: "absolute", right: 6, bottom: bottom / 2 - 6, fontFamily: MONO, fontSize: 8.5,
            fontWeight: 700, color: A.green }}>{Math.round((bottom / h) * 100)}%</div>
        </>
      )}
      <div style={{ position: "absolute", left: 7, top: 7, fontFamily: MONO, fontSize: 9, fontWeight: 700,
        color: A.ink3, background: A.soft, padding: "2px 6px", borderRadius: 3 }}>Edited</div>
    </div>
  );
}

// ─── small atoms ─────────────────────────────────────────────────────────────
// A Notion tag: 3px radius, sentence case, normal weight, no letterspacing. The
// old version was an uppercase 800-weight capsule, which is the single loudest
// thing an interface can wear.
const Pill = ({ t, bg, fg }: { t: string; bg: string; fg: string }) => (
  <span style={{ fontSize: 12, fontWeight: 500, padding: "2px 7px", borderRadius: 3,
    background: bg, color: fg, whiteSpace: "nowrap", lineHeight: 1.4 }}>{t}</span>
);
const WAITING = { bg: A.amberBg, fg: A.amber };
const OK = { bg: A.greenBg, fg: A.green };
const REDO = { bg: A.redBg, fg: A.red };
const RUNNING = { bg: A.softer, fg: A.ink2 };

// Notion's buttons: primary is a solid blue with a 4px radius; secondary is
// white with the same hairline as everything else, NOT a 1.5px coloured outline.
const Btn = ({ label, primary, color, dim }: { label: string; primary?: boolean; color?: string; dim?: boolean }) => (
  <span style={{ display: "inline-flex", alignItems: "center", fontSize: 14, fontWeight: 500,
    padding: "6px 12px", borderRadius: 4, whiteSpace: "nowrap", opacity: dim ? .4 : 1,
    color: primary ? "#FFF" : A.ink, background: primary ? (color || A.ink) : A.panel,
    border: primary ? "none" : `1px solid ${A.border}`,
    boxShadow: primary ? "none" : "rgba(15,15,15,0.05) 0 1px 2px" }}>{label}</span>
);

// ─── the chat panel ──────────────────────────────────────────────────────────
// The agent speaks first, and it asks rather than announces. A designer who has
// never opened a terminal should be able to answer every question on this panel
// in their own words.
type Msg = {
  from: "agent" | "you";
  text: string;
  plan?: string[];
  planEdit?: number;      // index of a plan line that just changed
  wrote?: string;         // "↳ wrote to the standard" style footer
  typing?: boolean;
};

function Chat({ msgs, placeholder }: { msgs: Msg[]; placeholder: string }) {
  return (
    // Light, like the rest. The agent's turns sit in a grey block — Notion's
    // callout — and the designer's sit in a blue-tinted one. No dark panel, no
    // shouted AGENT label; who is speaking is carried by the emoji and the
    // alignment, which is enough.
    <div style={{ width: CHAT_W, flexShrink: 0, background: A.panel, borderLeft: `1px solid ${A.border}`,
      display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${A.border}`, display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ fontSize: 16 }}>🖼</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: A.ink }}>Designer agent</div>
          <div style={{ fontSize: 12, color: A.ink3 }}>reads the standard before it edits</div>
        </div>
      </div>
      <div style={{ flex: 1, padding: 14, display: "flex", flexDirection: "column", gap: 10, overflow: "hidden" }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.from === "you" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: 296, fontSize: 13.5, lineHeight: 1.55, padding: "9px 12px", borderRadius: 4,
              background: m.from === "you" ? A.softer : A.soft, color: A.ink,
              display: "flex", gap: 8, alignItems: "flex-start" }}>
              {m.from === "agent" && <span style={{ fontSize: 13, lineHeight: 1.5 }}>🖼</span>}
              <div>
                {m.text}
                {m.plan && (
                  <div style={{ marginTop: 9, background: A.panel, border: `1px solid ${A.border}`, borderRadius: 4,
                    padding: "9px 11px", display: "flex", flexDirection: "column", gap: 5 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: A.ink3 }}>What I plan to do</div>
                    {m.plan.map((p, j) => (
                      <div key={j} style={{ fontSize: 13, lineHeight: 1.45, display: "flex", gap: 7,
                        color: j === m.planEdit ? A.ink : A.ink2 }}>
                        <span style={{ color: j === m.planEdit ? A.green : A.ink3 }}>
                          {j === m.planEdit ? "✎" : String(j + 1) + "."}
                        </span>
                        <span>{p}</span>
                      </div>
                    ))}
                  </div>
                )}
                {m.typing && (
                  <div style={{ marginTop: 6, display: "flex", gap: 4 }}>
                    {[0, 1, 2].map((d) => (
                      <span key={d} style={{ width: 5, height: 5, borderRadius: 99, background: A.ink3, opacity: .35 + d * .25 }} />
                    ))}
                  </div>
                )}
                {m.wrote && (
                  <div style={{ marginTop: 7, fontSize: 12.5, color: A.green }}>↳ {m.wrote}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: 14, borderTop: `1px solid ${A.border}` }}>
        <div style={{ background: A.panel, border: `1px solid ${A.border}`, borderRadius: 4, padding: "9px 12px",
          fontSize: 13.5, color: A.ink3 }}>{placeholder}</div>
      </div>
    </div>
  );
}

// ─── the third panel: the standard, live ─────────────────────────────────────
// THE MOMENT THAT MATTERS. This is a document in the AI data layer — the agent's
// own AGENT.md — not a settings screen and not code. It is rendered as a file on
// purpose: a designer can read it, and so can Tomer.
type Line = { t: string; h?: boolean; n?: boolean; from?: string };

function Standard({ lines, saved }: { lines: Line[]; saved?: string }) {
  return (
    <div style={{ width: STD_W, flexShrink: 0, background: A.panel, borderLeft: `1px solid ${A.border}`,
      display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "12px 15px", borderBottom: `1px solid ${A.border}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: A.ink }}>product-images.md</div>
          <Pill t="data layer" bg={A.violetBg} fg={A.violet} />
        </div>
        <div style={{ marginTop: 3, fontSize: 12, color: A.ink3 }}>
          the agent reads this before every edit
        </div>
      </div>
      <div style={{ flex: 1, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4, overflow: "hidden" }}>
        {lines.map((l, i) => (
          <div key={i} style={{
            position: "relative", paddingLeft: l.n ? 10 : 0,
            boxShadow: l.n ? `inset 2px 0 0 ${A.green}` : "none",
            background: l.n ? A.greenBg : "transparent",
            borderRadius: l.n ? 3 : 0, marginLeft: l.n ? -4 : 0, paddingTop: l.n ? 3 : 0, paddingBottom: l.n ? 3 : 0,
          }}>
            <div style={{ fontFamily: MONO, fontSize: l.h ? 12.5 : 12, lineHeight: 1.55,
              fontWeight: l.h ? 600 : 400, color: l.h ? A.ink : A.ink2 }}>{l.t}</div>
            {l.n && l.from && (
              <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 400, color: A.green, marginTop: 2 }}>
                ✎ {l.from}
              </div>
            )}
          </div>
        ))}
      </div>
      {saved && (
        <div style={{ padding: "9px 14px", borderTop: `1px solid ${A.border}`, background: A.soft,
          fontSize: 12, color: A.green }}>{saved}</div>
      )}
    </div>
  );
}

// ─── the gallery ─────────────────────────────────────────────────────────────
type Card = {
  kind: Kind; name: string; state: "pending" | "working" | "review" | "approved" | "redo";
  pad?: number; guides?: boolean; note?: string;
};

function Empty({ text, sub }: { text: string; sub: string }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
      <div style={{ width: 54, height: 54, borderRadius: 6, background: A.soft, border: `1px solid ${A.border}`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🖼</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: A.ink }}>{text}</div>
      <div style={{ fontSize: 14, color: A.ink2, maxWidth: 400, textAlign: "center", lineHeight: 1.55 }}>{sub}</div>
    </div>
  );
}

/** The pair: what the supplier sent, and what the agent made of it. Side by side
 *  because the designer's judgement is a comparison, and the screen should ask
 *  the question the way they already answer it. */
function Pair({ card, big }: { card: Card; big?: boolean }) {
  const w = big ? PAIR_BIG : PAIR_SMALL, h = w;
  const tone = card.state === "approved" ? OK : card.state === "redo" ? REDO : card.state === "working" ? RUNNING : WAITING;
  const label = card.state === "approved" ? "approved" : card.state === "redo" ? "redoing" :
    card.state === "working" ? "working" : card.state === "review" ? "your call" : "queued";
  return (
    <div data-zoom style={{ background: A.panel, border: `1px solid ${A.border}`, borderRadius: 6, padding: 12,
      display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: A.ink2 }}>{card.name}</div>
        <Pill t={label} bg={tone.bg} fg={tone.fg} />
      </div>
      <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
        <RawShot kind={card.kind} w={w} h={h} />
        {card.state === "working" ? (
          <div style={{ width: w, height: h, borderRadius: 4, background: A.soft, border: `1px solid ${A.border}`,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 7 }}>
            <div style={{ display: "flex", gap: 4 }}>
              {[0, 1, 2].map((d) => (
                <span key={d} style={{ width: 6, height: 6, borderRadius: 99, background: A.ink3, opacity: .35 + d * .25 }} />
              ))}
            </div>
            <div style={{ fontSize: 13, color: A.ink3 }}>editing</div>
          </div>
        ) : (
          <CleanShot kind={card.kind} w={w} h={h} pad={card.pad} guides={card.guides} />
        )}
      </div>
      {card.note && (
        <div style={{ fontSize: 13, lineHeight: 1.5, color: A.ink2, background: A.soft, borderRadius: 3, padding: "7px 9px" }}>
          {card.note}
        </div>
      )}
      {card.state === "review" && (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Btn label="Approve" primary color={A.green} />
          <Btn label="Tell it what to change" />
        </div>
      )}
    </div>
  );
}

/** The queue strip: 100 files in, and what has happened to each. Deliberately
 *  countable — the designer always knows how much is left. */
function Queue({ done, total, running, grow }: { done: number; total: number; running?: boolean; grow?: boolean }) {
  return (
    // grow is passed only where the queue sits in a ROW beside the calibration
    // meter. Without it the box shrinks to its content and the batch name runs
    // straight into the count ("Dropbox0 of 40 done"); with it in a COLUMN it
    // would stretch vertically instead, which is why this is a prop and not a
    // default.
    <div style={{ flex: grow ? 1 : undefined, minWidth: 0, background: A.panel, border: `1px solid ${A.border}`,
      borderRadius: 6, padding: "11px 13px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: A.ink }}>
          Batch · new arrivals <span style={{ fontWeight: 500, color: A.ink3 }}>· Dropbox</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color: A.ink2 }}>
          {done} of {total} done
        </div>
      </div>
      <div style={{ display: "flex", gap: 3 }}>
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} style={{ flex: 1, height: 7, borderRadius: 2,
            background: i < done ? A.green : i === done && running ? A.ink3 : A.border }} />
        ))}
      </div>
    </div>
  );
}

// ─── the calibration meter ───────────────────────────────────────────────────
// The ladder, made visible. Three clean approvals and the agent is calibrated
// for this batch — and it says so rather than deciding quietly.
function Calibration({ n }: { n: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ fontSize: 13, color: A.ink3 }}>Calibration</div>
      <div style={{ display: "flex", gap: 4 }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{ width: 9, height: 9, borderRadius: 99,
            background: i < n ? A.green : A.border }} />
        ))}
      </div>
      <div style={{ fontSize: 13, color: n >= 3 ? A.green : A.ink3 }}>
        {n >= 3 ? "calibrated for this batch" : `${n} of 3 clean approvals`}
      </div>
    </div>
  );
}

// ─── the eight screens ───────────────────────────────────────────────────────
// The base standard: what came out of the sessions with their design team,
// BEFORE this batch teaches it anything. Written as a file because that is what
// it is. Illustrative — this is the shape of the artifact, not their real taste.
const BASE: Line[] = [
  { t: "# Product images — the standard", h: true },
  { t: "learned with the design team, editable by them" },
  { t: "" },
  { t: "## Frame", h: true },
  { t: "- square, 2000 x 2000, white #FFFFFF" },
  { t: "- product centred on the horizontal axis" },
  { t: "- 8% clear space left and right" },
  { t: "" },
  { t: "## The set, per product", h: true },
  { t: "1. hero, front, straight on" },
  { t: "2. three-quarter angle" },
  { t: "3. back panel, text legible" },
  { t: "4. scale shot, in a hand" },
  { t: "" },
  { t: "## Never", h: true },
  { t: "- never crop the packaging to fill the frame" },
  { t: "- never invent a shadow the photo does not have" },
];

/** The line the designer's correction adds, inserted where it belongs in the
 *  document rather than appended at the end — the agent files it, it does not
 *  just log it. */
const withWhitespace = (from: string): Line[] => {
  const out = [...BASE];
  out.splice(7, 0, { t: "- 12% clear space below the product", n: true, from });
  return out;
};
const withCap = (): Line[] => {
  const out = withWhitespace("added from your correction on image 1");
  out.splice(13, 0, { t: "- on angle shots keep the cap fully in frame", n: true, from: "added from your correction on image 2" });
  return out;
};

export type Screen = {
  n: number;
  key: string;
  title: string;
  caption: string;
  render: () => React.ReactNode;
};

const PLAN = [
  "read the standard, then look at all 100",
  "cut each product out of the supplier background",
  "place it on white, centred, 8% side margins",
  "build the 4-shot set per product",
  "flag anything the standard cannot decide",
];

const PLAN_EDITED = [
  "read the standard, then look at all 100",
  "cut each product out of the supplier background",
  "place it on white, centred, 8% side margins",
  "build the 4-shot set — hero, angle, back panel, scale",
  "leave the two damaged cartons for you, do not retouch",
];

function Body({ children }: { children: React.ReactNode }) {
  return (
    // overflow:hidden is a GUARD, not styling. Without it, anything wider than
    // the gallery column paints straight over the chat and standard panels
    // instead of being clipped — which is exactly how the step-7 row failed, and
    // it fails silently because the screen still renders.
    <div style={{ flex: 1, minWidth: 0, overflow: "hidden", padding: BODY_PAD,
      display: "flex", flexDirection: "column", gap: 13, background: A.appBg }}>
      {children}
    </div>
  );
}

const SCREENS: Screen[] = [
  {
    n: 1, key: "open",
    title: "The agent opens the conversation",
    caption:
      "The designer opens the mini-app and the agent has already asked the first question. Nothing to configure, no prompt to write, no run to schedule — the screen starts by asking what they are working on today.",
    render: () => (
      <>
        <Body>
          <Empty text="No batch open yet" sub="Tell the agent what you are working on and it will pull the files itself. It has the Dropbox, Shopify and warehouse connections already." />
        </Body>
        <Chat
          placeholder="Type your answer…"
          msgs={[{ from: "agent", text: "Morning. Which images are we editing today?" }]}
        />
        <Standard lines={BASE} />
      </>
    ),
  },
  {
    n: 2, key: "answer",
    title: "They answer in their own words",
    caption:
      "\"We have 100 new images in Dropbox.\" No folder picker, no file IDs, no syntax. The agent resolves that sentence against the connectors it already has and reports what it found — 100 files, 25 products, 4 shots each.",
    render: () => (
      <>
        <Body>
          <Queue done={0} total={100} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
            {(["jar", "pouch", "bottle", "box", "jar", "bottle"] as Kind[]).map((k, i) => (
              <div key={i} style={{ opacity: .55 }}>
                <RawShot kind={k} w={150} h={150} />
              </div>
            ))}
            <div style={{ width: 150, height: 150, borderRadius: 8, background: A.panel, border: `1px dashed ${A.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: A.ink3 }}>
              +94
            </div>
          </div>
        </Body>
        <Chat
          placeholder="Type your answer…"
          msgs={[
            { from: "agent", text: "Morning. Which images are we editing today?" },
            { from: "you", text: "We have 100 new images in Dropbox, the new arrivals folder." },
            { from: "agent", text: "Found it — 100 files, shot yesterday. They cover 25 products. Give me a moment to look at them against the standard." , typing: true },
          ]}
        />
        <Standard lines={BASE} />
      </>
    ),
  },
  {
    n: 3, key: "plan",
    title: "It says what it plans to do — before it does anything",
    caption:
      "The agent has read all 100 and the standard, and now states its plan and waits. This is the screen that answers the adoption fear: nothing has happened to a single file yet, and a non-programmer can read every line of what is about to.",
    render: () => (
      <>
        <Body>
          <Queue done={0} total={100} />
          <div style={{ background: A.panel, border: `1px solid ${A.border}`, borderRadius: 6, padding: 14,
            display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: A.ink }}>What it found in the 100</div>
            {[
              ["25 products", "4 shots each, as expected"],
              ["all 100 on the supplier's brown surface", "every one needs the background replaced"],
              ["14 have a second product in frame", "will be cropped out, packaging kept whole"],
              ["2 cartons look damaged", "not a photo problem — flagged for you, not retouched"],
            ].map(([a, b], i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
                <div style={{ width: 260, fontSize: 12.5, fontWeight: 650, color: A.ink }}>{a}</div>
                <div style={{ fontSize: 12, color: A.ink2 }}>{b}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 9 }}>
            <Btn label="Looks right, start" primary color={A.green} />
            <Btn label="Change the plan" />
          </div>
        </Body>
        <Chat
          placeholder="Tell it what to change…"
          msgs={[
            { from: "you", text: "We have 100 new images in Dropbox, the new arrivals folder." },
            {
              from: "agent",
              text: "I read all 100 against the standard. Here is what I would do — say the word and I will start, or change any line.",
              plan: PLAN,
            },
          ]}
        />
        <Standard lines={BASE} />
      </>
    ),
  },
  {
    n: 4, key: "pushback",
    title: "The designer pushes back, and the plan changes",
    caption:
      "\"Leave the damaged cartons alone.\" The plan is not a formality — the designer edits it in a sentence and the agent restates it. The order matters: they argue with the plan while it is still cheap, not with 40 finished images.",
    render: () => (
      <>
        <Body>
          <Queue done={0} total={100} />
          <div style={{ background: A.panel, border: `1px solid ${A.border}`, borderRadius: 6, padding: 14,
            display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: A.ink }}>The plan, updated</div>
              <Pill t="changed by you" bg="#EDE6F7" fg={A.violet} />
            </div>
            {PLAN_EDITED.map((p, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: i === 4 ? A.violet : A.ink3 }}>{i + 1}</span>
                <span style={{ fontSize: 12.5, color: i === 4 ? A.ink : A.ink2, fontWeight: i === 4 ? 650 : 400 }}>{p}</span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 9 }}>
            <Btn label="Start on the first one" primary color={A.green} />
            <Btn label="Change the plan" />
          </div>
        </Body>
        <Chat
          placeholder="Tell it what to change…"
          msgs={[
            { from: "agent", text: "Here is what I would do — say the word and I will start, or change any line.", plan: PLAN },
            { from: "you", text: "Don't touch the damaged cartons. Leave them for us and tell us which ones." },
            {
              from: "agent",
              text: "Updated. I will list the two and skip them entirely rather than retouch them.",
              plan: PLAN_EDITED, planEdit: 4,
            },
          ]}
        />
        <Standard lines={BASE} />
      </>
    ),
  },
  {
    n: 5, key: "first",
    title: "One image. Then it stops.",
    caption:
      "The agent does the first image and waits, original beside edited, at the size the judgement actually needs. It does not run ahead. This is the first rung of the ladder, and the screen is deliberately quiet: one decision, two buttons.",
    render: () => (
      <>
        <Body>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Queue done={0} total={100} running grow />
            <div style={{ paddingLeft: 16 }}><Calibration n={0} /></div>
          </div>
          <div style={{ display: "flex", gap: 13 }}>
            <Pair big card={{ kind: "jar", name: "IMG_4417.jpg · hero", state: "review",
              note: "Background replaced, product centred, packaging kept whole. The neighbouring box is out of frame." }} />
            <div style={{ flex: 1, background: A.panel, border: `1px solid ${A.border}`, borderRadius: 6, padding: 14,
              display: "flex", flexDirection: "column", gap: 9 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>What it did, and why</div>
              {[
                ["cut out and placed on white", "frame · white #FFFFFF"],
                ["centred, 8% side margins", "frame · 8% clear space"],
                ["kept the whole carton in frame", "never · do not crop packaging"],
                ["kept the original shadow direction", "never · do not invent a shadow"],
              ].map(([a, b], i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <div style={{ fontSize: 12, color: A.ink }}>{a}</div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: A.violet }}>↳ {b}</div>
                </div>
              ))}
              <div style={{ marginTop: "auto", fontSize: 11, color: A.ink3, lineHeight: 1.5 }}>
                Every line traces to a rule in the standard on the right. Nothing here is the agent's own taste.
              </div>
            </div>
          </div>
        </Body>
        <Chat
          placeholder="Approve, or tell it what to change…"
          msgs={[
            { from: "you", text: "Don't touch the damaged cartons." },
            { from: "agent", text: "Here is the first one. I have stopped — tell me if this is right before I touch the other 99." },
          ]}
        />
        <Standard lines={BASE} />
      </>
    ),
  },
  {
    n: 6, key: "correction",
    title: "A correction — and the standard changes, on screen",
    caption:
      "\"More white space at the bottom.\" Watch the third panel: a line appears in the standard, filed under Frame where it belongs, tagged with where it came from. The taste is not buried in a model — it is a document their designer can read, argue with, and edit.",
    render: () => (
      <>
        <Body>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Queue done={0} total={100} running grow />
            <div style={{ paddingLeft: 16 }}><Calibration n={0} /></div>
          </div>
          <div style={{ display: "flex", gap: 13 }}>
            <Pair big card={{ kind: "jar", name: "IMG_4417.jpg · hero", state: "review", pad: 30, guides: true,
              note: "Redone with 12% below the product. The rule is now in the standard, so the other 99 will be made this way." }} />
            <div style={{ flex: 1, background: A.panel, border: `1px solid ${A.border}`, borderRadius: 6, padding: 14,
              display: "flex", flexDirection: "column", gap: 11 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>Your correction became a rule</div>
              <div style={{ background: A.greenBg, borderRadius: 3, padding: "10px 12px" }}>
                <div style={{ fontSize: 11.5, color: A.ink2, lineHeight: 1.5 }}>you said</div>
                <div style={{ fontSize: 13, fontWeight: 650, color: A.ink, marginTop: 2 }}>"more white space at the bottom"</div>
                <div style={{ marginTop: 9, fontSize: 11.5, color: A.ink2 }}>it wrote, under <b>Frame</b></div>
                <div style={{ fontFamily: MONO, fontSize: 11.5, color: A.green, marginTop: 2 }}>- 12% clear space below the product</div>
              </div>
              <div style={{ fontSize: 11.5, color: A.ink2, lineHeight: 1.55 }}>
                It applies from the next image, not just this one. Correct it once and it is corrected for the batch,
                for the next batch, and for whoever opens this file in six months.
              </div>
              <div style={{ marginTop: "auto", display: "flex", gap: 8 }}>
                <Btn label="Approve" primary color={A.green} />
                <Btn label="Not quite yet" />
              </div>
            </div>
          </div>
        </Body>
        <Chat
          placeholder="Approve, or tell it what to change…"
          msgs={[
            { from: "agent", text: "Here is the first one. I have stopped — tell me if this is right." },
            { from: "you", text: "Almost. More white space at the bottom, it's sitting too low." },
            {
              from: "agent",
              text: "Redone at 12% below the product. I have put that in the standard so it holds for the rest.",
              wrote: "wrote 1 line to product-images.md",
            },
          ]}
        />
        <Standard lines={withWhitespace("added from your correction on image 1")} saved="product-images.md · updated just now" />
      </>
    ),
  },
  {
    n: 7, key: "ladder",
    title: "Second. Third. One more correction, then clean.",
    caption:
      "Image two needs one small correction; image three needs none. The meter fills — and it fills because the agent got better, not because a counter ticked. Three rungs, three approvals, and the designer has spent about two minutes.",
    render: () => (
      <>
        <Body>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Queue done={3} total={100} running grow />
            <div style={{ paddingLeft: 16 }}><Calibration n={3} /></div>
          </div>
          <div style={{ display: "flex", gap: 11 }}>
            <Pair card={{ kind: "jar", name: "01 · hero", state: "approved", pad: 19,
              note: "1 correction — white space" }} />
            <Pair card={{ kind: "pouch", name: "02 · angle", state: "approved", pad: 19,
              note: "1 correction — cap in frame" }} />
            <Pair card={{ kind: "bottle", name: "03 · back panel", state: "approved", pad: 19,
              note: "no corrections" }} />
          </div>
        </Body>
        <Chat
          placeholder="Approve, or tell it what to change…"
          msgs={[
            { from: "you", text: "On the angle shot keep the whole cap in frame." },
            { from: "agent", text: "Noted and in the standard. Here is the third.", wrote: "wrote 1 line to product-images.md" },
            { from: "you", text: "That one's good, nothing to change." },
            { from: "agent", text: "Three in a row with the last one clean. I think I have your standard for this batch." },
          ]}
        />
        <Standard lines={withCap()} saved="product-images.md · 2 lines added this session" />
      </>
    ),
  },
  {
    n: 8, key: "batch",
    title: "Now it asks for the go-ahead — and runs the other 97",
    caption:
      "Only now does it offer to run the rest, and it still asks. The images land as they are made, every one still openable and rejectable, and the two damaged cartons come back as a question rather than a retouch. Supervised, supervised, supervised — then trusted.",
    render: () => (
      <>
        <Body>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Queue done={46} total={100} running grow />
            <div style={{ paddingLeft: 16 }}><Calibration n={3} /></div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
            {([
              ["jar", "approved"], ["pouch", "approved"], ["bottle", "approved"], ["box", "approved"],
              ["jar", "approved"], ["bottle", "approved"], ["pouch", "approved"], ["jar", "working"],
            ] as [Kind, Card["state"]][]).map(([k, st], i) => (
              <div key={i} style={{ background: A.panel, border: `1px solid ${A.border}`, borderRadius: 6, padding: 8,
                display: "flex", flexDirection: "column", gap: 6 }}>
                {st === "working" ? (
                  <div style={{ width: 132, height: 132, borderRadius: 7, background: A.soft, border: `1px dashed ${A.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: A.ink3 }}>editing</div>
                ) : (
                  <CleanShot kind={k} w={132} h={132} pad={16} />
                )}
                <Pill t={st === "working" ? "working" : "done"} bg={st === "working" ? RUNNING.bg : OK.bg}
                  fg={st === "working" ? RUNNING.fg : OK.fg} />
              </div>
            ))}
            <div style={{ width: 150, height: 164, borderRadius: 6, background: A.soft, border: `1px solid ${A.border}`,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: A.ink2 }}>54 to go</div>
              <div style={{ fontSize: 11, color: A.ink3 }}>about 4 minutes</div>
            </div>
          </div>
          <div style={{ background: A.amberBg, borderRadius: 3, padding: "10px 13px",
            display: "flex", alignItems: "center", gap: 10 }}>
            <Pill t="for you" bg="#F7E4DE" fg={A.red} />
            <div style={{ fontSize: 12, color: A.ink2 }}>
              <b>2 cartons look damaged</b> — IMG_4462 and IMG_4471. Skipped, as you asked. They need a person, not an edit.
            </div>
          </div>
        </Body>
        <Chat
          placeholder="Ask it anything…"
          msgs={[
            { from: "agent", text: "Three in a row and the last one clean. Shall I do the other 97 the same way?" },
            { from: "you", text: "Yes, go." },
            {
              from: "agent",
              text: "Running. They will appear as they finish and you can reject any of them. The two damaged cartons are set aside for you.",
            },
          ]}
        />
        <Standard lines={withCap()} saved="product-images.md · 2 lines added this session" />
      </>
    ),
  },
];

export const DESIGNER_SCREENS = SCREENS;

// ─── the screen shell ────────────────────────────────────────────────────────
export function DesignerScreen({ step, scale = 1 }: { step: number; scale?: number }) {
  const s = SCREENS[Math.max(0, Math.min(SCREENS.length - 1, step))];
  const inner = (
    <div style={{
      width: SCREEN_W, height: SCREEN_H, display: "flex", overflow: "hidden",
      background: A.appBg, fontFamily: FONT, borderRadius: 8,
      border: `1px solid ${A.border}`, boxShadow: "rgba(15,15,15,0.05) 0 1px 3px",
    }}>
      <Rail />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* topbar — the PROTOTYPE chip is not decoration. This goes in front of a
            CTO, and a mock that does not say it is a mock is how you lose one. */}
        <div style={{ height: 46, flexShrink: 0, background: A.panel, borderBottom: `1px solid ${A.border}`,
          display: "flex", alignItems: "center", padding: "0 16px", gap: 12 }}>
          <div style={{ fontSize: 12.5, color: A.ink3 }}>Mini-apps</div>
          <div style={{ fontSize: 12.5, color: A.ink3 }}>/</div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: A.ink }}>Designer</div>
          <Pill t="prototype · illustrative" bg="#F1F2F4" fg={A.ink3} />
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontFamily: MONO, fontSize: 11, color: A.ink3 }}>step {s.n} of 8</div>
            <span style={{ width: 24, height: 24, borderRadius: 99, background: A.softer, color: A.ink2,
              display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 500 }}>D</span>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: "flex" }}>{s.render()}</div>
      </div>
    </div>
  );
  if (scale === 1) return inner;
  return (
    <div style={{ width: SCREEN_W * scale, height: SCREEN_H * scale, overflow: "hidden" }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>{inner}</div>
    </div>
  );
}

/** A titled screen for the filmstrip: the number, what it shows, and why it is
 *  in the flow. Erez reads the caption; the client will hear it narrated. */
export function DesignerPlate({ step, scale = 0.62 }: { step: number; scale?: number }) {
  const s = SCREENS[step];
  return (
    <div data-zoom style={{ fontFamily: FONT, display: "flex", flexDirection: "column", gap: 12, width: SCREEN_W * scale }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 22, fontWeight: 500, color: A.ink3 }}>{String(s.n).padStart(2, "0")}</span>
        <span style={{ fontSize: 26, fontWeight: 700, color: A.ink, letterSpacing: "-.018em" }}>{s.title}</span>
      </div>
      <div style={{ fontSize: 15, lineHeight: 1.55, color: A.ink2, maxWidth: SCREEN_W * scale }}>{s.caption}</div>
      <DesignerScreen step={step} scale={scale} />
    </div>
  );
}

/** The same eight screens, driven by a real click. The rhythm is the pitch, so
 *  it has to be felt at speed, not read as a wall. React Flow traps handled:
 *  nowheel/nodrag/nopan + stopPropagation, and the NODE carries pointerEvents
 *  auto and a zIndex above the pane (see Everything420BoardPage). */
export function DesignerStepper() {
  const [i, setI] = useState(0);
  const s = SCREENS[i];
  const go = (n: number) => setI(Math.max(0, Math.min(SCREENS.length - 1, n)));
  return (
    <div
      className="nowheel nodrag nopan"
      onPointerDown={(e) => e.stopPropagation()}
      style={{ fontFamily: FONT, display: "flex", flexDirection: "column", gap: 14 }}
    >
      {/* maxWidth is load-bearing, not cosmetic. This lives in an inline-block
          SectionBox with no width constraint, so without it flex-wrap never
          wraps: the eight tabs lay out in ONE long row, the box grows to ~4000px
          and the whole demo band stops fitting beside the filmstrip. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", maxWidth: SCREEN_W }}>
        {SCREENS.map((sc, n) => (
          <div key={sc.key} onClick={() => go(n)} style={{
            cursor: "pointer", padding: "7px 12px", borderRadius: 4,
            background: n === i ? A.ink : A.panel,
            border: `1px solid ${n === i ? A.ink : A.border}`,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 500, color: n === i ? "rgba(255,255,255,.6)" : A.ink3 }}>
              {String(sc.n).padStart(2, "0")}
            </span>
            <span style={{ fontSize: 13.5, fontWeight: 650, color: n === i ? "#FFF" : A.ink2 }}>{sc.title}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div onClick={() => go(i - 1)} style={{ cursor: "pointer" }}><Btn label="← Back" dim={i === 0} /></div>
        <div onClick={() => go(i + 1)} style={{ cursor: "pointer" }}><Btn label="Next →" primary dim={i === SCREENS.length - 1} /></div>
        <div style={{ fontSize: 14, color: A.ink2, lineHeight: 1.5, maxWidth: SCREEN_W - 330 }}>{s.caption}</div>
      </div>
      <DesignerScreen step={i} />
    </div>
  );
}
